import * as React from 'react'
import {Util, UUID} from 'yougile-util'
import {ClickOutside} from '../../common/click-outside'
import {renderStateful, StatefulData} from './stateful'

interface Props {
  handlerId: UUID
  open?: boolean
  forceOpen?: boolean
  onClose?: () => void
  children?: React.ReactNode
  content?: () => JSX.Element | null
}

interface State {
  open: boolean
}

export class Closable extends React.Component<Props> {
  private closableEl: HTMLElement | null
  private clickoutHandler: UUID | undefined
  private handlerId: UUID | undefined

  private static toggleHandlers: Map<UUID, () => void> = new Map()
  private static closeHandlers: Map<UUID, () => void> = new Map()

  public static makeId(): UUID {
    return Util.UUID()
  }

  public static toggle(id: UUID): void {
    const cb = Closable.toggleHandlers.get(id)
    if (cb) {
      cb()
    }
  }

  public static close(id: UUID): void {
    const cb = Closable.closeHandlers.get(id)
    if (cb) {
      cb()
    }
  }

  public constructor(props: Props) {
    super(props)
  }

  public override componentWillUnmount(): void {
    Closable.toggleHandlers.delete(this.handlerId)
    Closable.closeHandlers.delete(this.handlerId)
    if (this.clickoutHandler) {
      ClickOutside.cancel(this.clickoutHandler)
      this.clickoutHandler = undefined
    }
  }

  public override render(): React.ReactNode {
    const initial = {open: this.props.open}

    const draw = (data: StatefulData<State>) => {
      const state = data.getState()
      const close = () => {
        data.setState({open: false})
        if (this.props.onClose) {
          this.props.onClose()
        }
      }
      const setClickout = () => {
        if (this.clickoutHandler) {
          ClickOutside.cancel(this.clickoutHandler)
        }
        this.clickoutHandler = ClickOutside.once({
          element: () => this.closableEl || data.getEl(),
          callback: close
        })
      }

      if (this.handlerId) {
        Closable.toggleHandlers.delete(this.handlerId)
        Closable.closeHandlers.delete(this.handlerId)
      }
      this.handlerId = this.props.handlerId

      Closable.toggleHandlers.set(this.handlerId, () => {
        if (!state.open) {
          setTimeout(setClickout, 100)
        } else {
          if (this.props.onClose) {
            this.props.onClose()
          }
        }
        data.setState({open: !state.open})
      })
      Closable.closeHandlers.set(this.handlerId, () => {
        data.setState({open: false})
        if (this.props.onClose) {
          this.props.onClose()
        }
      })
      if (state.open || this.props.forceOpen) {
        const ref = (el: HTMLElement | null) => (this.closableEl = el)
        return (
          <div ref={ref}>{this.props.content?.() || this.props.children}</div>
        )
      } else {
        if (this.clickoutHandler) {
          ClickOutside.cancel(this.clickoutHandler)
          this.clickoutHandler = undefined
        }
        return <div />
      }
    }
    return renderStateful(initial, draw)
  }
}
